// Odznaki = wyzwania z /api/challenges przełożone na stany z Legendy:
// notStarted (niezaczęta / zablokowana), inProgress (w trakcie), earned (zdobyta)
// + opcjonalny wariant szlachetny (złota poświata).
import { COUNTRY_FORMS } from '@/utils/countries'
import { PEAK_FORMS } from '@/utils/format'
import { challengeRule } from '@/utils/peakRules'

// Flaga "rzadka" / szlachetna. BE jeszcze jej nie zwraca — do tego czasu każda
// odznaka jest zwykła. Gdy /api/challenges zacznie zwracać to pole (true),
// odznaka automatycznie dostanie wariant szlachetny, bez zmian w UI.
export const NOBLE_FLAG_FIELD = 'is_rare'

export function isNobleChallenge(challenge) {
  return Boolean(challenge) && challenge[NOBLE_FLAG_FIELD] === true
}

// Poziomy wyzwania (BE: bronze → silver → gold; ukończone dopiero na najwyższym)
export const LEVEL_LABELS = { bronze: 'brąz', silver: 'srebro', gold: 'złoto' }

const STATUS_LABELS = {
  earned: 'zdobyta',
  inProgress: 'w trakcie',
  locked: 'zablokowana',
  notStarted: 'niezaczęta'
}

// Glif logotypu odznaki (theme: ikony badge-*) dobierany po nazwie wyzwania
const GLYPH_RULES = [
  [/świat|world|siedem/i, 'summits'],
  [/europ/i, 'globe'],
  [/beskid/i, 'hills'],
  [/sudet/i, 'range'],
  [/karkono|tatr|bieszcz/i, 'ridge']
]

export function badgeGlyph(challenge) {
  const name = String(challenge?.name || '')
  const rule = GLYPH_RULES.find(([pattern]) => pattern.test(name))
  return rule ? rule[1] : 'peak'
}

function requiredPeakIds(challenge) {
  const peaks = challenge?.conditions?.specific_peaks?.peaks
  return Array.isArray(peaks) && peaks.length ? peaks : null
}

// Licznik "zdobyte/wszystkie": dla specific_peaks z listy szczytów, dla
// pojedynczego progu (np. total_ascents ≥ 10) z postępu procentowego BE.
function progressCounts(challenge, completed, climbedPeakIds) {
  const percent = Math.max(0, Math.min(100, Number(challenge.progress) || 0))
  const peakIds = requiredPeakIds(challenge)

  // peaks_count / countries_count: dokładne liczniki z BE (details.counts)
  const rule = challengeRule(challenge.conditions)
  const counts = rule && challenge.userProgress?.counts?.[rule.key]
  if (counts && Number(counts.total) > 0) {
    const total = Number(counts.total)
    return { done: completed ? total : Math.min(total, Number(counts.done) || 0), total }
  }

  if (peakIds) {
    const total = peakIds.length
    const fromAscents = peakIds.filter(id => climbedPeakIds.has(id)).length
    const peaksPercent = Number(challenge.userProgress?.specific_peaks)
    const fromBackend = Number.isFinite(peaksPercent) ? Math.round((peaksPercent / 100) * total) : 0
    return { done: completed ? total : Math.min(total, Math.max(fromAscents, fromBackend)), total }
  }

  const conditions = Object.values(challenge.conditions || {})
  if (conditions.length === 1) {
    const target = Number(conditions[0]?.value)
    if (target > 0) {
      return { done: completed ? target : Math.min(target, Math.floor((percent / 100) * target)), total: target }
    }
  }
  return { done: null, total: null }
}

function unitForms(challenge) {
  const rule = challengeRule(challenge.conditions)
  if (rule) return rule.key === 'countries_count' ? COUNTRY_FORMS : PEAK_FORMS
  return requiredPeakIds(challenge) ? PEAK_FORMS : null
}

export function toBadge(challenge, { climbedPeakIds = new Set(), completedIds = new Set() } = {}) {
  const completed = challenge.status === 'completed'
  const percent = Math.max(0, Math.min(100, Number(challenge.progress) || 0))
  const { done, total } = progressCounts(challenge, completed, climbedPeakIds)

  let fraction = percent / 100
  if (completed) fraction = 1
  else if (total) fraction = done / total

  let state = 'notStarted'
  if (completed) state = 'earned'
  else if (fraction > 0 || percent > 0) state = 'inProgress'

  const requires = Array.isArray(challenge.requires_challenges) ? challenge.requires_challenges : []
  const locked = state === 'notStarted' && requires.some(id => !completedIds.has(id))

  const valueLabel = total && total <= 999
    ? `${done}/${total}`
    : `${Math.round(fraction * 100)}%`

  return {
    id: challenge.id,
    name: challenge.name,
    description: challenge.description || '',
    glyph: badgeGlyph(challenge),
    noble: Boolean(challenge.noble),
    state,
    locked,
    fraction,
    done,
    total,
    valueLabel,
    level: LEVEL_LABELS[challenge.level] ? challenge.level : null,
    statusLabel: STATUS_LABELS[locked ? 'locked' : state] + (LEVEL_LABELS[challenge.level] ? ` · ${LEVEL_LABELS[challenge.level]}` : ''),
    peakIds: requiredPeakIds(challenge),
    // Warunek peaks_count / countries_count (lista pasujących szczytów w arkuszu)
    rule: challengeRule(challenge.conditions),
    // Odmiana jednostki licznika: szczyty / kraje (null = cel liczbowy bez jednostki)
    unitForms: unitForms(challenge),
    // Dane szczytów z /api/challenges; null = starsze API, trzeba sięgnąć do katalogu /api/locations
    peaks: requiredPeakIds(challenge) && Array.isArray(challenge.peaks) ? challenge.peaks : null
  }
}

export function toBadges(challenges, ascents = []) {
  const climbedPeakIds = new Set(ascents.map(ascent => ascent.location_id))
  const completedIds = new Set(challenges.filter(c => c.status === 'completed').map(c => c.id))
  return challenges.map(challenge => toBadge(challenge, { climbedPeakIds, completedIds }))
}

const STATE_ORDER = { inProgress: 0, earned: 1, notStarted: 2 }

// W trakcie (najbliżej ukończenia) → zdobyte → niezaczęte → zablokowane
export function sortBadges(badges) {
  return [...badges].sort((a, b) =>
    STATE_ORDER[a.state] - STATE_ORDER[b.state] ||
    Number(a.locked) - Number(b.locked) ||
    b.fraction - a.fraction ||
    String(a.name).localeCompare(String(b.name), 'pl')
  )
}
